import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import PromptStep from "../components/steps/PromptStep";
import TopicStep from "../components/steps/TopicStep";
import FileStep from "../components/steps/DriveStep";
import YoutubeStep from "../components/steps/YoutubeStep";
import QuestionDistributionForm from "../components/forms/QuestionDistributionForm";
import LearningOutcomeStep from "../components/steps/LearningOutcomeStep";
import {
  createHomeworkAssignment,
  createQuizAssignment,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import {
  CreateHomeworkAssignmentRequestBody,
  CreateQuizAssignmentRequestBody,
} from "@packages/api/madlenAPI.schemas";
import { getEnvironment } from "@packages/utils";
import { Button, Text } from "../../../../core";
import { trackEvent } from "@packages/hooks/useAnalytics";

const AssignmentGeneratorView = () => {
  const { type } = useParams<{ type: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [assignmentData, setAssignmentData] = useState<
    (CreateHomeworkAssignmentRequestBody | CreateQuizAssignmentRequestBody) & {
      hwAssignmentType: "HOMEWORK" | "QUIZ";
    }
  >({
    assignmentDifficulty: "MEDIUM",
    courseType:
      i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN",
    description: "",
    driveFileIds: [],
    duration: 30,
    endDate: new Date(
      new Date().getTime() + 2 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    environment: getEnvironment(),
    generator: "PROMPT",
    grade: 8,
    hwAssignmentType: "HOMEWORK",
    contentLanguage: i18n.language as "tr" | "en" | "de" | "fr" | "es",
    name: "",
    prompt: "",
    questionStyle: "",
    questionTypes: [
      {
        questionType: "FB",
        count: 3, // Default to 3
        point: 2,
        difficulty: "MEDIUM",
      },
      {
        questionType: "OE",
        count: 3, // Default to 3
        point: 10,
        difficulty: "MEDIUM",
      },
      {
        questionType: "TF",
        count: 3, // Default to 3
        point: 1,
        difficulty: "MEDIUM",
      },
      {
        questionType: "TEST",
        count: 3, // Default to 3
        point: 5,
        difficulty: "MEDIUM",
      },
    ],
    topicIds: [],
    learningOutcomeIds: [],
    maarifLearningOutcomeIds: [],
    youtubeLink: "",
  });

  const handleUpdateAssignmentData = (
    updates: Partial<CreateHomeworkAssignmentRequestBody>,
  ) => {
    // No need to enforce minimum count here, allow 0
    setAssignmentData((prev) => ({ ...prev, ...updates }));
  };

  const renderStepContent = () => {
    switch (type) {
      case "prompt":
        return (
          <>
            <PromptStep
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
            <QuestionDistributionForm
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
          </>
        );
      case "topic_list":
        return (
          <>
            <TopicStep
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
            <QuestionDistributionForm
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
          </>
        );
      case "drive_file":
        return (
          <>
            <FileStep
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
            <QuestionDistributionForm
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
          </>
        );
      case "youtube_link":
        return (
          <>
            <YoutubeStep
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
            <QuestionDistributionForm
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
          </>
        );
      case "learning_outcome":
        return (
          <>
            <LearningOutcomeStep
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
            <QuestionDistributionForm
              assignmentData={assignmentData}
              onChange={handleUpdateAssignmentData}
            />
          </>
        );
      default:
        return <div>Invalid generation type</div>;
    }
  };

  const validatePromptGeneration = () => {
    if (!assignmentData.prompt || assignmentData.prompt.trim() === "") {
      addToast({
        title: t("notifications.assignment.emptyPrompt"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateTopicGeneration = () => {
    if (!assignmentData.topicIds?.length) {
      addToast({
        title: t("notifications.assignment.emptyTopics"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateDriveGeneration = () => {
    if (
      !assignmentData.driveFileIds?.length &&
      !assignmentData.teacherDocumentIds?.length
    ) {
      addToast({
        title: t("notifications.assignment.emptyFiles"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateYoutubeGeneration = () => {
    if (!assignmentData.youtubeLink?.trim()) {
      addToast({
        title: t("notifications.assignment.emptyYoutubeLink"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const validateLearningOutcomeGeneration = () => {
    if (
      !assignmentData.learningOutcomeIds?.length &&
      !assignmentData.maarifLearningOutcomeIds?.length
    ) {
      addToast({
        title: t("notifications.assignment.emptyLearningOutcomes"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  // Updated validation to check if total question count is 0
  const validateQuestionCounts = () => {
    const totalQuestionCount = assignmentData.questionTypes.reduce(
      (total, qType) => total + (qType.count ?? 0),
      0,
    );

    if (totalQuestionCount === 0) {
      addToast({
        title: t("notifications.assignment.zeroQuestions"),
        variant: "error",
        duration: 3000,
      });
      return false;
    }
    return true;
  };

  const getValidationFunction = () => {
    switch (type) {
      case "prompt":
        return validatePromptGeneration;
      case "topic_list":
        return validateTopicGeneration;
      case "drive_file":
        return validateDriveGeneration;
      case "youtube_link":
        return validateYoutubeGeneration;
      case "learning_outcome":
        return validateLearningOutcomeGeneration;
      default:
        return () => false;
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleGenerate = async () => {
    const isQuiz = assignmentData.hwAssignmentType === "QUIZ";
    const isDurationExceeded =
      ((assignmentData as CreateQuizAssignmentRequestBody)?.duration ?? 0) >
      1440;
    if (isQuiz && isDurationExceeded) {
      addToast({
        title: t("notifications.assignment.invalidDuration"),
        variant: "error",
        duration: 3000,
      });
      return;
    }

    // Validate total question counts first
    if (!validateQuestionCounts()) {
      return;
    }

    // Then validate the specific generation type
    const validationFunction = getValidationFunction();
    const isValid = validationFunction();

    if (!isValid) return;

    setLoading(true);
    try {
      // Remove question types with 0 count before sending to API
      const filteredQuestionTypes = assignmentData.questionTypes.filter(
        (qType) => (qType.count ?? 0) > 0,
      );

      //Prompt is being trimmed before sending
      const body: (
        | CreateHomeworkAssignmentRequestBody
        | CreateQuizAssignmentRequestBody
      ) & {
        hwAssignmentType: "HOMEWORK" | "QUIZ";
      } = JSON.parse(
        JSON.stringify({
          ...assignmentData,
          questionTypes: filteredQuestionTypes,
          prompt: assignmentData.prompt?.trim(),
          endDate:
            assignmentData.hwAssignmentType === "HOMEWORK"
              ? (assignmentData as CreateHomeworkAssignmentRequestBody).endDate
              : null,
          duration:
            assignmentData.hwAssignmentType === "QUIZ"
              ? (assignmentData as CreateQuizAssignmentRequestBody).duration
              : //assignment duration for homework is removed because we removed start date we could only have endDate now
                null,
        }),
      );

      if (body.topicIds && body.topicIds?.length > 0) {
        body.generator = "TOPIC_LIST";
      } else if (
        (body.learningOutcomeIds && body.learningOutcomeIds?.length > 0) ||
        (body.maarifLearningOutcomeIds &&
          body.maarifLearningOutcomeIds?.length > 0)
      ) {
        const savedValue = localStorage.getItem("isMaarifEnabled");
        const isMaarif = savedValue ? JSON.parse(savedValue) : false;
        body.generator =
          isMaarif || assignmentData.grade < 5
            ? "MAARIF_LEARNING_OUTCOME"
            : "LEARNING_OUTCOME";
      } else if (body.youtubeLink) {
        body.generator = "YOUTUBE_LINK";
      } else if (body.driveFileIds && body.driveFileIds?.length > 0) {
        body.generator = "DRIVE_FILE";
      } else if (
        body.teacherDocumentIds &&
        body.teacherDocumentIds?.length > 0
      ) {
        body.generator = "TEACHER_DOCUMENT";
      } else {
        body.generator = "PROMPT";
      }
      let newAssignment;
      if (body.hwAssignmentType === "QUIZ") {
        newAssignment = await createQuizAssignment(body);
      } else {
        newAssignment = await createHomeworkAssignment(body);
      }
      console.log("newAssignment", newAssignment);
      track(
        {
          category: "ContentGeneration",
          action: "single.assignment.generate",
          label: "Single assingment generation click",
          nonInteraction: false,
        },
        {
          location: "/single-assignment/generate",
          element: "generate-button",

          singleAssignmentCourseType: assignmentData.courseType,
          singleAssignmentDifficulty: assignmentData.assignmentDifficulty,
          singleAssignmentDuration:
            assignmentData.hwAssignmentType === "QUIZ"
              ? (assignmentData as CreateQuizAssignmentRequestBody).duration
              : null,
          singleAssignmentEndDate:
            assignmentData.hwAssignmentType === "HOMEWORK"
              ? (assignmentData as CreateHomeworkAssignmentRequestBody).endDate
              : null,
          singleAssignmentGeneratorType: assignmentData.generator,
          singleAssignmentPrompt: assignmentData.prompt,
        },
      );
      addToast({
        title: t("notifications.assignment.createSuccess"),
        variant: "success",
        duration: 3000,
      });
      setLoading(false);
      navigate(`/assignments/${newAssignment.id}/edit`);
    } catch (error: any) {
      console.log("error", error);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        setLoading(false);
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        addToast({
          title: t("notifications.assignment.createError"),
          variant: "error",
          duration: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/assignments/generate");
  };

  return (
    <div className="container mx-auto px-4 py-4 h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <Text variant="h2" className="mb-4">
            {t("assignments.generator.steps.properties")}
          </Text>

          {renderStepContent()}
        </div>

        <div className="mt-4 flex justify-between">
          <Button
            onClick={handleBack}
            size="md"
            rounded="md"
            bgColor="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            textColor="text-gray-700 dark:text-gray-200"
            variant="outline"
            className="border-gray-300 dark:border-gray-600"
          >
            {t("common.back")}
          </Button>
          <Button
            onClick={handleGenerate}
            size="sm"
            rounded="md"
            bgColor="bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600"
            textColor="text-white"
            className={`px-4 ${loading ? "opacity-50 cursor-not-allowed hover:bg-indigo-600 dark:hover:bg-indigo-500" : ""}`}
            disabled={loading}
            data-testid="create-assignment-from-prompt-next-button"
          >
            {t("common.generate")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentGeneratorView;
